import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import { NavIcon } from "./navIcons";
import { NavGroup, scoreMatch } from "./navModel";
import { NoMatch, SearchField, SectionLabel } from "./navPrimitives";
import { NAV, overlayPaper, resetRow, rowSx } from "./navTheme";
import { MAX_SHORTCUTS, resetShortcuts, toggleShortcut } from "./shortcutStore";

interface ManageShortcutsProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  groups: NavGroup[];
  pinnedIds: string[];
  onClose: () => void;
}

/** Pin/unpin panel behind the shortcut row's "+" tile. */
const ManageShortcuts: React.FC<ManageShortcutsProps> = ({
  open,
  anchorEl,
  groups,
  pinnedIds,
  onClose,
}) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const visibleGroups = useMemo(() => {
    const q = query.trim();
    if (!q) return groups;
    return groups
      .map((group) => ({
        ...group,
        pages: group.pages.filter(
          (page) =>
            scoreMatch(q, page.label) > 0 || scoreMatch(q, group.label) > 0,
        ),
      }))
      .filter((group) => group.pages.length > 0);
  }, [groups, query]);

  const atLimit = pinnedIds.length >= MAX_SHORTCUTS;

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      marginThreshold={8}
      slotProps={{
        transition: { onEntered: () => inputRef.current?.focus() },
        paper: {
          sx: {
            ...overlayPaper(16),
            mt: 1,
            width: 296,
            maxWidth: "calc(100vw - 24px)",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          pt: 1.25,
          pb: 1,
        }}
      >
        <SectionLabel>
          Shortcuts {pinnedIds.length}/{MAX_SHORTCUTS}
        </SectionLabel>
        <Box
          component="button"
          type="button"
          onClick={() => resetShortcuts()}
          sx={{
            ...resetRow,
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: NAV.textMuted,
            px: 0.75,
            py: 0.25,
            borderRadius: "8px",
            "&:hover": { color: NAV.textActive, backgroundColor: NAV.hover },
          }}
        >
          Reset
        </Box>
      </Box>

      <Box sx={{ px: 1.5, pb: 1 }}>
        <SearchField
          value={query}
          onChange={setQuery}
          onKeyDown={(event) => {
            // Swallow Escape only while clearing, so the popover still closes.
            if (event.key === "Escape" && query) {
              event.stopPropagation();
              setQuery("");
            }
          }}
          placeholder="Find a page…"
          ariaLabel="Find a page to pin"
          height={36}
          iconSize={16}
          radius={10}
          autoFocus
          inputRef={inputRef}
        />
      </Box>

      <Box sx={{ maxHeight: 340, overflowY: "auto", pb: 1 }}>
        {visibleGroups.length === 0 && <NoMatch query={query} py={2.5} />}

        {visibleGroups.map((group) => (
          <Box key={group.id}>
            <SectionLabel
              sx={{ px: 1.75, pt: 1, pb: 0.5, fontSize: "0.625rem" }}
            >
              {group.label}
            </SectionLabel>

            {group.pages.map((page) => {
              const isPinned = pinnedIds.includes(page.id);
              const disabled = !isPinned && atLimit;
              return (
                <Box
                  key={page.id}
                  component="button"
                  type="button"
                  role="switch"
                  aria-checked={isPinned}
                  disabled={disabled}
                  onClick={() => toggleShortcut(page.id)}
                  sx={{
                    ...rowSx({ height: 38, radius: 0, px: 1.5 }),
                    width: "100%",
                    color: isPinned ? NAV.textActive : NAV.textIdle,
                    opacity: disabled ? 0.35 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                    "@media (hover: hover)": {
                      "&:hover": {
                        backgroundColor: disabled ? "transparent" : NAV.hover,
                      },
                    },
                  }}
                >
                  <NavIcon
                    id={page.id}
                    groupId={page.groupId}
                    size={17}
                    color="inherit"
                  />
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: "0.8125rem",
                      fontWeight: isPinned ? 600 : 500,
                      color: "inherit",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {page.label}
                  </Typography>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 18,
                      height: 18,
                      borderRadius: "6px",
                      display: "grid",
                      placeItems: "center",
                      border: `1px solid ${isPinned ? NAV.accentBorder : NAV.border}`,
                      backgroundColor: isPinned ? NAV.accentSoft : "transparent",
                      color: NAV.accent,
                    }}
                  >
                    {isPinned && <CheckRounded sx={{ fontSize: 13 }} />}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Popover>
  );
};

export default ManageShortcuts;
